import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Package, Search, ChevronLeft, ChevronRight, Plus, Edit2, Trash2, Building2, DollarSign, Filter, X } from 'lucide-react';

const CATEGORY_LABELS = {
  sheet: 'Roofing Sheet', tile: 'Tiles', ridge: 'Ridge', valley: 'Valley',
  barge: 'Barge', spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing',
  cleaning: 'Cleaning', recoat: 'Recoat', aquaseal: 'Aquaseal/Dektite',
  safety: 'Safety', travel: 'Travel', urgent: 'Urgent', misc: 'Miscellaneous', underlay: 'Underlay',
};

export default function ProductCatalogPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('products');
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products?page=${page}&limit=${limit}&search=${search}&category=${category}&supplier=${supplierFilter}`);
      setProducts(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, limit, search, category, supplierFilter]);

  const loadMeta = useCallback(async () => {
    try {
      const [catRes, supRes, statsRes] = await Promise.all([
        api.get('/products/categories'),
        api.get('/suppliers'),
        api.get('/products/stats/summary'),
      ]);
      setCategories(catRes.data);
      setSuppliers(supRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const saveProduct = async (formData) => {
    if (editProduct?._id) {
      await api.put(`/products/${editProduct._id}`, formData);
    } else {
      await api.post('/products', formData);
    }
    setShowForm(false);
    setEditProduct(null);
    loadProducts();
    loadMeta();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`);
    loadProducts();
    loadMeta();
  };

  const saveSupplier = async (formData) => {
    if (editSupplier?._id) {
      await api.put(`/suppliers/${editSupplier._id}`, formData);
    } else {
      await api.post('/suppliers', formData);
    }
    setShowSupplierForm(false);
    setEditSupplier(null);
    loadMeta();
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    await api.delete(`/suppliers/${id}`);
    loadMeta();
  };

  return (
    <div data-testid="product-catalog-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Materials & Pricing</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{stats?.total_products || 0}</span> products across <span className="font-mono-num font-bold text-slate-700">{categories.length}</span> categories from <span className="font-mono-num font-bold text-slate-700">{stats?.total_suppliers || 0}</span> suppliers</p>
        </div>
        <div className="flex gap-2">
          {tab === 'suppliers' && (
            <button data-testid="add-supplier-btn" onClick={() => { setEditSupplier(null); setShowSupplierForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#4A6FA5] text-white rounded-md text-sm font-semibold hover:bg-[#3D5D8A] shadow-sm hover:shadow-md hover:shadow-blue-500/20 transition-all">
              <Plus size={16} strokeWidth={2.5} /> Add Supplier
            </button>
          )}
          <button data-testid="add-product-btn" onClick={() => { setEditProduct(null); setShowForm(true); setTab('products'); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000] shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
            <Plus size={16} strokeWidth={2.5} /> Add Product
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-[#E2E2DF] rounded-md p-0.5 w-fit shadow-sm">
        <button onClick={() => setTab('products')} className={`px-4 py-1.5 rounded text-xs font-semibold transition-all ${tab === 'products' ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>Product Catalog (<span className="font-mono-num">{total}</span>)</button>
        <button onClick={() => setTab('suppliers')} className={`px-4 py-1.5 rounded text-xs font-semibold transition-all ${tab === 'suppliers' ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>Suppliers (<span className="font-mono-num">{suppliers.length}</span>)</button>
      </div>

      {tab === 'products' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="product-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
            </div>
            <select data-testid="category-filter" value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.category} value={c.category}>{CATEGORY_LABELS[c.category] || c.category} ({c.count})</option>)}
            </select>
            <select value={supplierFilter} onChange={e => { setSupplierFilter(e.target.value); setPage(1); }} className="text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 bg-white">
              <option value="">All Suppliers</option>
              {stats?.by_supplier?.map(s => <option key={s._id} value={s._id}>{s._id} ({s.count})</option>)}
            </select>
            {(search || category || supplierFilter) && (
              <button onClick={() => { setSearch(''); setCategory(''); setSupplierFilter(''); setPage(1); }} className="text-xs text-slate-500 hover:text-red-500"><X size={14} /></button>
            )}
          </div>

          {/* Products Table */}
          <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="text-left px-3 py-2.5">Product</th>
                    <th className="text-left px-3 py-2.5">Category</th>
                    <th className="text-left px-3 py-2.5">Supplier</th>
                    <th className="text-right px-3 py-2.5">Buy Cost</th>
                    <th className="text-right px-3 py-2.5">Materials</th>
                    <th className="text-right px-3 py-2.5">Labour</th>
                    <th className="text-right px-3 py-2.5">Contractor</th>
                    <th className="text-right px-3 py-2.5">Margin %</th>
                    <th className="text-center px-3 py-2.5 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => {
                    const marginPct = p.margin_percent ?? 0;
                    const isDefault = p.margin_is_default === true;
                    return (
                      <tr key={p._id} className="border-t border-[#E2E2DF] hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2.5 text-sm font-medium text-slate-800 max-w-[250px]">
                          <div className="truncate">{p.name}</div>
                          {(p.cover_width_mm > 0 || (p.unit_type && p.unit_type !== 'each')) && (
                            <div className="flex gap-1.5 mt-0.5">
                              {p.unit_type && p.unit_type !== 'each' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">per {p.unit_type}</span>}
                              {p.cover_width_mm > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{p.cover_width_mm}mm cover ({((p.coverage_factor - 1) * 100).toFixed(0)}% extra)</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{CATEGORY_LABELS[p.category] || p.category}</span></td>
                        <td className="px-3 py-2.5 text-sm text-slate-600">{p.supplier || '-'}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono-num">${p.buy_cost_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono-num">${p.materials_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono-num">${p.labour_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono-num">${p.contractor_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono-num">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isDefault ? 'bg-amber-100 text-amber-700 border border-amber-300' : marginPct >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                            {marginPct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex gap-1 justify-center">
                            <button data-testid={`edit-product-${p._id}`} onClick={() => { setEditProduct(p); setShowForm(true); }} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                            <button data-testid={`delete-product-${p._id}`} onClick={() => deleteProduct(p._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E2DF]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Showing {((page-1)*limit)+1}-{Math.min(page*limit, total)} of {total}</span>
                <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="text-xs border border-[#E2E2DF] rounded px-2 py-1 bg-white">
                  <option value={25}>25/page</option>
                  <option value={50}>50/page</option>
                  <option value={100}>100/page</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Page {page}/{pages}</span>
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronLeft size={16} /></button>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'suppliers' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map(s => (
            <div key={s._id} className="bg-white border border-[#E2E2DF] rounded-xl p-5 hover:border-slate-300 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#D35400]/10 flex items-center justify-center"><Building2 size={18} className="text-[#D35400]" /></div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">{s.name}</h3>
                    {s.code && <p className="text-xs text-slate-400">Code: {s.code}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-supplier-${s._id}`} onClick={() => { setEditSupplier(s); setShowSupplierForm(true); }} className="p-1 text-slate-300 hover:text-[#D35400]"><Edit2 size={14} /></button>
                  <button data-testid={`delete-supplier-${s._id}`} onClick={() => deleteSupplier(s._id)} className="p-1 text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
              {s.contact_person && <p className="text-xs text-slate-600 mt-3">Contact: {s.contact_person}</p>}
              {s.email && <p className="text-xs text-slate-500">{s.email}</p>}
              {s.phone && <p className="text-xs text-slate-500">{s.phone}</p>}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-[#D35400] font-medium">{s.product_count || 0} products</span>
                <button onClick={() => { setTab('products'); setSupplierFilter(s.name); setPage(1); }} className="text-xs text-[#D35400] hover:underline">View products</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showForm && <ProductFormModal product={editProduct} categories={Object.entries(CATEGORY_LABELS)} suppliers={suppliers} onSave={saveProduct} onClose={() => { setShowForm(false); setEditProduct(null); }} />}

      {/* Add/Edit Supplier Modal */}
      {showSupplierForm && <SupplierFormModal supplier={editSupplier} onSave={saveSupplier} onClose={() => { setShowSupplierForm(false); setEditSupplier(null); }} />}
    </div>
  );
}


function SupplierFormModal({ supplier, onSave, onClose }) {
  const [form, setForm] = useState(supplier || { name: '', code: '', contact_person: '', email: '', phone: '', fax: '', address: '', rtr_code: '', notes: '', active: true });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div data-testid="supplier-form-modal" className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold text-[#2C2C2C] mb-4">{supplier?._id ? 'Edit Supplier' : 'Add New Supplier'}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Supplier Name *</label>
              <input data-testid="supplier-name-input" value={form.name} onChange={e => update('name', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" placeholder="e.g. Metalcraft" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Code</label>
              <input value={form.code} onChange={e => update('code', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" placeholder="e.g. MC" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Contact Person</label>
              <input value={form.contact_person} onChange={e => update('contact_person', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Phone</label>
              <input value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Fax</label>
              <input value={form.fax} onChange={e => update('fax', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Address</label>
            <input value={form.address} onChange={e => update('address', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => update('notes', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button data-testid="save-supplier-btn" onClick={() => onSave(form)} disabled={!form.name.trim()} className="flex-1 px-4 py-2 bg-[#4A6FA5] text-white rounded-lg text-sm font-medium hover:bg-[#3D5D8A] disabled:opacity-50">Save Supplier</button>
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}


function ProductFormModal({ product, categories, suppliers, onSave, onClose }) {
  const ROOF_TYPES = ['Concrete Tile', 'Clay Tile', 'Modern Concrete', 'Old Concrete', 'Long Run Iron', 'Short Run Iron', 'Iron', 'Tin Tile', 'Decramastic', 'Butynol', 'Polycarbonate', 'Asbestos', 'Metal Tile', 'Shake/Shingle', 'Slate', 'Corrugate', 'Tray', 'Glass'];
  const [form, setForm] = useState(product || {
    category: 'sheet', name: '', buy_cost_rate: 0, materials_rate: 0, labour_rate: 0, contractor_rate: 0,
    supplier: '', description: '', labour_description: '',
    unit_type: 'each', cover_width_mm: 0, coverage_factor: 1.0, gauge: '', wastage_percent: 5.0,
    roof_types: [],
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleRoofType = (rt) => {
    setForm(f => ({...f, roof_types: (f.roof_types || []).includes(rt) ? f.roof_types.filter(r => r !== rt) : [...(f.roof_types || []), rt]}));
  };

  // Auto-calculate coverage factor when cover_width_mm changes
  const updateCoverWidth = (mm) => {
    const coverMm = parseInt(mm) || 0;
    let factor = 1.0;
    if (coverMm > 0) {
      // Coverage factor = sheet_width / cover_width (approximation: assumes ~900mm standard sheet)
      // But really the factor is just stored and user can adjust. Auto-suggest based on common roofing patterns.
      // Standard: 860mm sheet with 762mm cover = 860/762 = 1.129
      factor = coverMm > 0 ? parseFloat((860 / coverMm).toFixed(3)) : 1.0;
      if (factor < 1.0) factor = 1.0;
    }
    setForm(f => ({ ...f, cover_width_mm: coverMm, coverage_factor: factor }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div data-testid="product-form-modal" className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold text-[#2C2C2C] mb-4">{product?._id ? 'Edit Product' : 'Add Product'}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Category</label>
              <select value={form.category} onChange={e => update('category', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2">
                {categories.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Supplier</label>
              <input value={form.supplier} onChange={e => update('supplier', e.target.value)} list="supplier-list" className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
              <datalist id="supplier-list">{suppliers.map(s => <option key={s._id} value={s.name} />)}</datalist>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Product Name</label>
            <input data-testid="product-name-input" value={form.name} onChange={e => update('name', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" placeholder="e.g. Corrugate Galv /mtr (0.40Gauge 762mm)" />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Buy Cost Rate ($)</label>
              <input type="number" step="0.01" value={form.buy_cost_rate} onChange={e => update('buy_cost_rate', parseFloat(e.target.value) || 0)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Materials Rate ($)</label>
              <input type="number" step="0.01" value={form.materials_rate} onChange={e => update('materials_rate', parseFloat(e.target.value) || 0)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Labour Rate ($)</label>
              <input type="number" step="0.01" value={form.labour_rate} onChange={e => update('labour_rate', parseFloat(e.target.value) || 0)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Contractor Rate ($)</label>
              <input type="number" step="0.01" value={form.contractor_rate} onChange={e => update('contractor_rate', parseFloat(e.target.value) || 0)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
            </div>
          </div>

          {/* Margin */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Materials Margin %</label>
              <input data-testid="margin-percent-input" type="number" step="0.1" value={form.margin_percent ?? (form.buy_cost_rate > 0 && form.materials_rate > 0 ? ((form.materials_rate - form.buy_cost_rate) / form.materials_rate * 100).toFixed(1) : 5)} onChange={e => { update('margin_percent', parseFloat(e.target.value) || 0); update('margin_is_default', false); }} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
            </div>
            <div className="flex items-end pb-2">
              {form.margin_is_default && <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300 font-medium">Default 5% — not set from historic data</span>}
            </div>
          </div>

          {/* Coverage & Unit Settings */}
          <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">Coverage & Unit Settings</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Unit Type</label>
                <select data-testid="unit-type-select" value={form.unit_type || 'each'} onChange={e => update('unit_type', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2">
                  <option value="each">Each</option>
                  <option value="lm">Linear Metre (lm)</option>
                  <option value="m2">Square Metre (m2)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Cover Width (mm)</label>
                <input data-testid="cover-width-input" type="number" step="1" value={form.cover_width_mm || 0} onChange={e => updateCoverWidth(e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" placeholder="e.g. 762" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Coverage Factor</label>
                <input data-testid="coverage-factor-input" type="number" step="0.001" value={form.coverage_factor || 1.0} onChange={e => update('coverage_factor', parseFloat(e.target.value) || 1.0)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Default Wastage %</label>
                <input type="number" step="1" value={form.wastage_percent || 5} onChange={e => update('wastage_percent', parseFloat(e.target.value) || 5)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2 font-mono-num" />
              </div>
            </div>
            {(form.cover_width_mm > 0) && (
              <div className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                When this product is selected in the Quote Builder, coverage will auto-calculate: <span className="font-bold">{((form.coverage_factor - 1) * 100).toFixed(1)}% extra</span> material needed due to {form.cover_width_mm}mm overlap width. Wastage of {form.wastage_percent}% added on top.
              </div>
            )}
          </div>

          {/* Roof Types */}
          <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-800">Applicable Roof Types <span className="text-blue-400 font-normal">({(form.roof_types || []).length} selected)</span></h3>
            <div className="flex flex-wrap gap-1.5">
              {ROOF_TYPES.map(rt => (
                <button key={rt} type="button" onClick={() => toggleRoofType(rt)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${(form.roof_types || []).includes(rt) ? 'bg-blue-600 text-white border-blue-600' : 'border-blue-200 text-blue-400 hover:border-blue-400'}`}>
                  {rt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Gauge</label>
            <input value={form.gauge || ''} onChange={e => update('gauge', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" placeholder="e.g. 0.40" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Description</label>
            <input value={form.description} onChange={e => update('description', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Labour Description</label>
            <input value={form.labour_description} onChange={e => update('labour_description', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" />
          </div>

          {/* Margin Preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
            <div className="flex justify-between"><span>Revenue (Mat + Lab):</span><span className="font-mono-num font-bold">${((form.materials_rate || 0) + (form.labour_rate || 0)).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Cost (Buy + Con):</span><span className="font-mono-num">${((form.buy_cost_rate || 0) + (form.contractor_rate || 0)).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 mt-1"><span className="font-bold">Margin:</span><span className={`font-mono-num font-bold ${((form.materials_rate || 0) + (form.labour_rate || 0)) - ((form.buy_cost_rate || 0) + (form.contractor_rate || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(((form.materials_rate || 0) + (form.labour_rate || 0)) - ((form.buy_cost_rate || 0) + (form.contractor_rate || 0))).toFixed(2)}</span></div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button data-testid="save-product-btn" onClick={() => onSave(form)} className="flex-1 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]">Save</button>
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
